/**
 * Asset preloader — looks ahead in the beat list and preloads images/audio
 * so transitions feel instant.
 */

import type { Beat } from '../script/schema';

/** How many beats ahead to scan for preloadable assets. */
const LOOKAHEAD = 8;

/** Set of URLs already preloaded (or in-flight) this session. */
const preloaded = new Set<string>();

/**
 * Preload an image by creating a hidden Image element.
 * Returns a promise that resolves when loaded (or rejects silently).
 */
function preloadImage(src: string): void {
  if (preloaded.has(src)) return;
  preloaded.add(src);
  const img = new Image();
  img.src = src;
}

/**
 * Preload an audio file via a cache-warm fetch.
 * Uses fetch() with cache:'force-cache' so the response is stored in the
 * HTTP cache without injecting any DOM nodes into <head>.
 */
function preloadAudio(src: string): void {
  if (preloaded.has(src)) return;
  preloaded.add(src);
  void fetch(src, { cache: 'force-cache' }).catch(() => { /* ignore — best-effort prefetch */ });
}

/**
 * Given the full beat list and the current beat index, preload assets
 * from the next LOOKAHEAD beats.
 */
export function preloadUpcoming(beats: readonly Beat[], currentIndex: number): void {
  const end = Math.min(currentIndex + LOOKAHEAD + 1, beats.length);

  for (let i = currentIndex + 1; i < end; i++) {
    const beat = beats[i];

    switch (beat.kind) {
      case 'say':
        if (beat.portrait) preloadImage(`./${beat.portrait}`);
        if (beat.bg) {
          preloadBgPlates(beat.bg);
        }
        break;

      case 'scene':
        preloadBgPlates(beat.bg);
        break;

      case 'sfx':
        preloadAudio(`./${beat.sfx}`);
        break;

      // Other beat types have no preloadable assets
      default:
        break;
    }
  }
}

/**
 * Preload a background image.
 *
 * Previously this speculatively requested 6 parallax-plate variants
 * (-bg / -mid / -fg × .webp / .png) + the authored path = 7 image objects
 * per BG. None of the shipped backgrounds use the plate convention, so 6/7
 * requests 404'd while still allocating Image objects + leaking ~9GB over
 * a long playthrough. Now we only preload what is authored. If the parallax
 * pipeline ever ships, gate plate preloading on a feature flag or sniff
 * once via HEAD before warming all variants.
 */
function preloadBgPlates(bgPath: string): void {
  preloadImage(`./${bgPath}`);
}

/** Clear the preload cache (useful for tests). */
export function resetPreloadCache(): void {
  preloaded.clear();
}
